<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import { ZuordnungTechnologieStromerzeugung } from '@/models/ZuordnungTechnologieStromerzeugung';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import KpTextField from '@/components/KpTextField.vue';
import KpTechnologieStromerzeugungSelect from '@/components/KpTechnologieStromerzeugungSelect.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<ZuordnungTechnologieStromerzeugung | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    technologie_pronovo: createValidator(
        computed(() => record.value?.technologie_pronovo),
        [validateNotEmpty],
    ),
    technologie_stromerzeugung: createValidator(
        computed(() => record.value?.technologie_stromerzeugung_id),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(ZuordnungTechnologieStromerzeugung);
    } else {
        try {
            const rec = await ZuordnungTechnologieStromerzeugung.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_prod_zuordnung_technologie_stromerzeugung_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_prod_zuordnung_technologie_stromerzeugung' });
    }
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('zuordnung_technologien') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpTextField
                        v-model="record.technologie_pronovo"
                        :label="$t('technologie_pronovo')"
                        class="col-6"
                        fluid
                        :invalid="!validators.technologie_pronovo.isValid.value"
                        :error="validators.technologie_pronovo.msg.value"
                    ></KpTextField>
                    <KpTechnologieStromerzeugungSelect
                        class="col-6"
                        fluid
                        :label="$t('technologie_stromerzeugung')"
                        v-model="record.technologie_stromerzeugung_id"
                        :invalid="!validators.technologie_stromerzeugung.isValid.value"
                        :error="validators.technologie_stromerzeugung.msg.value"
                    ></KpTechnologieStromerzeugungSelect>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
